import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Box, Button, Card, CardContent, ButtonGroup } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import exportarEnviosRecepcionesAExcel from './ExcelEnviosRecepciones'; // <-- ¡Esta es la línea clave!

// Helper para formatear fechas a YYYY-MM-DD
const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Helper para obtener el inicio de la semana (Lunes) en formato YYYY-MM-DD
const getStartOfWeek = (dateString) => {
    const d = new Date(dateString + 'T00:00:00'); // Asegura que se interprete como hora local para evitar problemas de timezone
    const day = d.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajusta para que el lunes sea el inicio de la semana
    const monday = new Date(d.setDate(diff));
    return formatDate(monday);
};

// Helper para obtener el inicio del mes en formato YYYY-MM
const getStartOfMonth = (dateString) => {
    const d = new Date(dateString + 'T00:00:00');
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};


const BarEnviosRecepciones = () => {
    const [empresaIdEmpresa, setEmpresaIdEmpresa] = useState(null);
    const [rawEnviosData, setRawEnviosData] = useState([]); // Almacena los datos diarios crudos
    const [rawRecepcionesData, setRawRecepcionesData] = useState([]); // Almacena los datos diarios crudos
    const [groupBy, setGroupBy] = useState('day'); // 'day', 'week', 'month'
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const obtenerEnviosURL = 'https://invio.datachile.site/Web/Reportes/ObtenerEnviados.php';
    const obtenerRecepcionesURL = 'https://invio.datachile.site/Web/Reportes/ObtenerRecibidos.php';

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('usuario'));
        if (userData?.empresa_idEmpresa) {
            setEmpresaIdEmpresa(userData.empresa_idEmpresa);
            // Cargar los datos crudos una vez al inicio
            fetchRawData(userData.empresa_idEmpresa);
        } else {
            setError("No se pudo obtener el ID de empresa del usuario.");
            setIsLoading(false);
        }
    }, []); // Este useEffect se ejecuta solo una vez al montar el componente

    // Función para obtener los datos diarios crudos de las APIs
    const fetchRawData = async (empresaId) => {
        setIsLoading(true);
        setError(null);
        try {
            const enviosResponse = await axios.post(obtenerEnviosURL, { empresa_idEmpresa: empresaId });
            setRawEnviosData(enviosResponse.data);

            const recepcionesResponse = await axios.post(obtenerRecepcionesURL, { empresa_idEmpresa: empresaId });
            setRawRecepcionesData(recepcionesResponse.data);

        } catch (err) {
            console.error('Error al obtener los datos crudos:', err);
            setError("Hubo un error al cargar los datos. Inténtalo de nuevo.");
        } finally {
            setIsLoading(false);
        }
    };

    // Usamos useMemo para recalcular los datos agrupados solo cuando cambian
    // rawEnviosData, rawRecepcionesData o groupBy
    const { processedLabels, processedEnviosData, processedRecepcionesData } = useMemo(() => {
        const combineAndGroupData = (data, typeOfGroup) => {
            const grouped = {};
            data.forEach(item => {
                let groupKey;
                switch (typeOfGroup) {
                    case 'week':
                        groupKey = getStartOfWeek(item.fecha);
                        break;
                    case 'month':
                        groupKey = getStartOfMonth(item.fecha);
                        break;
                    case 'day':
                    default:
                        groupKey = item.fecha; // Ya viene como YYYY-MM-DD
                        break;
                }
                grouped[groupKey] = (grouped[groupKey] || 0) + item.cantidad;
            });
            return grouped;
        };

        // Agrupar los datos crudos según el tipo de agrupación seleccionado
        const groupedEnvios = combineAndGroupData(rawEnviosData, groupBy);
        const groupedRecepciones = combineAndGroupData(rawRecepcionesData, groupBy);

        // Obtener todas las claves de grupo (fechas/semanas/meses) de ambos conjuntos de datos
        const allGroupKeys = new Set([
            ...Object.keys(groupedEnvios),
            ...Object.keys(groupedRecepciones)
        ]);

        // Ordenar las claves de grupo cronológicamente
        const sortedLabels = Array.from(allGroupKeys).sort((a, b) => {
            // Para 'week' (YYYYWW), 'month' (YYYY-MM), 'day' (YYYY-MM-DD)
            // Una simple comparación de string funciona si el formato es consistente y ordenable
            return a.localeCompare(b);
        });

        // Mapear los datos agrupados a arrays para el BarChart
        const finalEnviosData = sortedLabels.map(key => groupedEnvios[key] || 0);
        const finalRecepcionesData = sortedLabels.map(key => groupedRecepciones[key] || 0);

        return {
            processedLabels: sortedLabels,
            processedEnviosData: finalEnviosData,
            processedRecepcionesData: finalRecepcionesData
        };
    }, [rawEnviosData, rawRecepcionesData, groupBy]); // Dependencias del useMemo

    const handleGroupByChange = (newGroupBy) => {
        setGroupBy(newGroupBy);
    };

    // Esta es la función que se llamará al hacer clic en el botón "Exportar a Excel"
    const handleExportExcel = () => {
        exportarEnviosRecepcionesAExcel({
            labels: processedLabels,
            enviosData: processedEnviosData,
            recepcionesData: processedRecepcionesData,
            groupBy: groupBy // Pasamos el tipo de agrupación actual para el nombre del archivo
        });
    };

    if (isLoading) {
        return (
            <Card sx={{ /* ... tus estilos de tarjeta ... */ }}>
                <CardContent>Cargando datos...</CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card sx={{ /* ... tus estilos de tarjeta ... */ }}>
                <CardContent>Error: {error}</CardContent>
            </Card>
        );
    }

    return (
        <Card
            sx={{
                backgroundColor: '#e3eaf0',
                boxShadow: 3,
                marginTop: 5,
                borderRadius: '16px',
                width: 760,
                height: 400, // Ajusta la altura para los botones
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <CardContent sx={{ width: '100%', height: '100%', padding: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ width: '100%', height: '100%' }}>
                    <h2>Envíos y Recepciones</h2>

                    {/* Botones de Agrupación */}
                    <ButtonGroup variant="contained" aria-label="outlined primary button group" sx={{ marginBottom: 2 }}>
                        <Button
                            onClick={() => handleGroupByChange('day')}
                            color={groupBy === 'day' ? 'primary' : 'inherit'}
                        >
                            Día
                        </Button>
                        <Button
                            onClick={() => handleGroupByChange('week')}
                            color={groupBy === 'week' ? 'primary' : 'inherit'}
                        >
                            Semana
                        </Button>
                        <Button
                            onClick={() => handleGroupByChange('month')}
                            color={groupBy === 'month' ? 'primary' : 'inherit'}
                        >
                            Mes
                        </Button>
                    </ButtonGroup>

                    <Box sx={{ width: '100%', height: '70%', marginTop: -1 }}>
                        {processedLabels.length > 0 ? (
                            <BarChart
                                xAxis={[{ scaleType: 'band', data: processedLabels }]}
                                series={[
                                    { data: processedEnviosData, label: 'Envíos', color: '#1bbac5' },
                                    { data: processedRecepcionesData, label: 'Recepciones', color: '#8884d8' }
                                ]}
                                width={750}
                                height={220}
                            />
                        ) : (
                            <p>No hay datos disponibles para mostrar el gráfico.</p>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ marginTop: 0 }}
                        onClick={handleExportExcel}
                    >
                        Exportar a Excel
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default BarEnviosRecepciones;